import api from "./axios";
import {
  AtualizarCatalogoRequest,
  BiOrganizacaoViewModel,
  CatalogoItemViewModel,
  ConexaoViewModel,
  CopiarAcessosResposta,
  CriarFiltroRequest,
  CriarGrantGrupoRequest,
  CriarGrantRequest,
  CriarGrantResposta,
  CriarLeitorRequest,
  CriarLeitorResposta,
  DefinirAbasRequest,
  DefinirMembrosRequest,
  DefinirPapelRequest,
  EmbedViewModel,
  EventosPagina,
  FiltroDadosViewModel,
  FiltroEventos,
  GrantGrupoViewModel,
  GrantViewModel,
  GrupoViewModel,
  IdResposta,
  ImportacaoResultado,
  ImportarAcessosRequest,
  LoginUrlViewModel,
  MatrizViewModel,
  MembroGrupoViewModel,
  MeusPaineisViewModel,
  PedidoAcessoRequest,
  PedidoAcessoResposta,
  PapelPaineis,
  PessoaPaineisViewModel,
  RegistrarEventoRequest,
  SalvarConexaoRequest,
  SalvarGrupoRequest,
  SalvarVisaoRequest,
  SyncResultadoViewModel,
  UsoResumoViewModel,
  VerComoViewModel,
  VisaoResumo,
  VisaoViewModel,
} from "../interfaces/Paineis";

/* ---- Leitor (permissão `bi.view`) ---- */

/** Painéis liberados para a pessoa na organização atual. */
export const getMeusPaineis = async (): Promise<MeusPaineisViewModel> =>
  (await api.get("/bi/reports")).data;

/** 404 = sem acesso; 503 = conexão indisponível. */
export const getEmbed = async (reportId: number): Promise<EmbedViewModel> =>
  (await api.get(`/bi/reports/${reportId}/embed`)).data;

/** 204. Relatório não liberado é ignorado pelo servidor. */
export const registrarEvento = async (evento: RegistrarEventoRequest): Promise<void> => {
  await api.post("/bi/events", evento);
};

/** Visões salvas só da pessoa da sessão; 404 sem acesso ao relatório. */
export const getVisoes = async (reportId: number): Promise<VisaoResumo[]> =>
  (await api.get(`/bi/reports/${reportId}/visoes`)).data;

/** 404 se a visão é de outra pessoa. */
export const getVisao = async (visaoId: number): Promise<VisaoViewModel> =>
  (await api.get(`/bi/visoes/${visaoId}`)).data;

/** 201 (nova) ou 200 (nome repetido substitui o estado). */
export const salvarVisao = async (reportId: number, dados: SalvarVisaoRequest): Promise<IdResposta> =>
  (await api.post(`/bi/reports/${reportId}/visoes`, dados)).data;

/** 204. 404 se de outra pessoa. */
export const excluirVisao = async (visaoId: number): Promise<void> => {
  await api.delete(`/bi/visoes/${visaoId}`);
};

/** 201. 404 relatório não publicado; 429 `{ message }` após 5 pedidos no dia. */
export const pedirAcesso = async (dados: PedidoAcessoRequest): Promise<PedidoAcessoResposta> =>
  (await api.post("/bi/pedidos-acesso", dados)).data;

/* ---- Administração (`bi.tenant.manage`; conexões exigem `bi.master`) ---- */

const ADMIN = "/bi/admin";

/** Master. Todos os clientes ativos; sem conexão = estado `NaoConfigurada`. */
export const getConexoes = async (): Promise<ConexaoViewModel[]> =>
  (await api.get(`${ADMIN}/connections`)).data;

/** Master. 204. Mudar tenant ou UPN zera o refresh token. */
export const salvarConexao = async (clientId: number, dados: SalvarConexaoRequest): Promise<void> => {
  await api.put(`${ADMIN}/connections/${clientId}`, dados);
};

/** Master. URL de consentimento da conta mestre. */
export const getLoginUrl = async (clientId: number): Promise<LoginUrlViewModel> =>
  (await api.get(`${ADMIN}/connections/${clientId}/login`)).data;

export const getCatalogo = async (clientId: number): Promise<CatalogoItemViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/catalog`)).data;

export const sincronizarCatalogo = async (clientId: number): Promise<SyncResultadoViewModel> =>
  (await api.post(`${ADMIN}/${clientId}/catalog/sync`)).data;

/** 204. */
export const atualizarCatalogo = async (
  clientId: number,
  reportId: number,
  dados: AtualizarCatalogoRequest
): Promise<void> => {
  await api.put(`${ADMIN}/${clientId}/catalog/${reportId}`, dados);
};

/** Prévia do painel para o admin (mesmo formato do embed do leitor). */
export const getEmbedPrevia = async (clientId: number, reportId: number): Promise<EmbedViewModel> =>
  (await api.get(`${ADMIN}/${clientId}/reports/${reportId}/embed`)).data;

/** Forma de login da organização (decide se "Cadastrar leitor" aparece). */
export const getOrganizacao = async (clientId: number): Promise<BiOrganizacaoViewModel> =>
  (await api.get(`${ADMIN}/${clientId}/organizacao`)).data;

export const getPessoasPaineis = async (clientId: number): Promise<PessoaPaineisViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/pessoas`)).data;

/** 204. `Admin` só o master concede; admin de tenant recebe 403. */
export const definirPapel = async (clientId: number, pessoaId: number, papel: PapelPaineis): Promise<void> => {
  const corpo: DefinirPapelRequest = { papel };
  await api.put(`${ADMIN}/${clientId}/pessoas/${pessoaId}/papel`, corpo);
};

/** 201. 400 se a organização não é de senha; 409 se o e-mail já existe. */
export const criarLeitor = async (clientId: number, dados: CriarLeitorRequest): Promise<CriarLeitorResposta> =>
  (await api.post(`${ADMIN}/${clientId}/leitores`, dados)).data;

/* ---- Admin: governança (fase 2) ---- */

export const getGrupos = async (clientId: number): Promise<GrupoViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/grupos`)).data;

/** 201. 409 "Já existe um grupo com este nome." */
export const criarGrupo = async (clientId: number, dados: SalvarGrupoRequest): Promise<IdResposta> =>
  (await api.post(`${ADMIN}/${clientId}/grupos`, dados)).data;

/** 204. */
export const renomearGrupo = async (clientId: number, grupoId: number, dados: SalvarGrupoRequest): Promise<void> => {
  await api.put(`${ADMIN}/${clientId}/grupos/${grupoId}`, dados);
};

/** 204. Desativa; concessões e filtros do grupo param de valer. */
export const excluirGrupo = async (clientId: number, grupoId: number): Promise<void> => {
  await api.delete(`${ADMIN}/${clientId}/grupos/${grupoId}`);
};

export const getMembrosGrupo = async (clientId: number, grupoId: number): Promise<MembroGrupoViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/grupos/${grupoId}/membros`)).data;

/** 204. Substitui os membros; 400 se alguma pessoa não pertence à organização. */
export const definirMembrosGrupo = async (
  clientId: number,
  grupoId: number,
  dados: DefinirMembrosRequest
): Promise<void> => {
  await api.put(`${ADMIN}/${clientId}/grupos/${grupoId}/membros`, dados);
};

export const getGrantsGrupo = async (clientId: number, reportId?: number): Promise<GrantGrupoViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/grants-grupo`, { params: { reportId } })).data;

/** 201. Reativa se existir; 400 relatório não publicado. */
export const criarGrantGrupo = async (clientId: number, dados: CriarGrantGrupoRequest): Promise<IdResposta> =>
  (await api.post(`${ADMIN}/${clientId}/grants-grupo`, dados)).data;

/** 204. */
export const revogarGrantGrupo = async (clientId: number, grantId: number): Promise<void> => {
  await api.delete(`${ADMIN}/${clientId}/grants-grupo/${grantId}`);
};

/** 204. `abas: null` = todas; 400 se alguma aba não existe no relatório. */
export const definirAbasGrant = async (clientId: number, grantId: number, dados: DefinirAbasRequest): Promise<void> => {
  await api.put(`${ADMIN}/${clientId}/grants/${grantId}/abas`, dados);
};

/** 204. Igual a `definirAbasGrant`, para concessão de grupo. */
export const definirAbasGrantGrupo = async (
  clientId: number,
  grantId: number,
  dados: DefinirAbasRequest
): Promise<void> => {
  await api.put(`${ADMIN}/${clientId}/grants-grupo/${grantId}/abas`, dados);
};

export const getFiltrosDados = async (clientId: number): Promise<FiltroDadosViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/filtros`)).data;

/** 201. 400 se não vier exatamente um entre pessoa e grupo. */
export const criarFiltroDados = async (clientId: number, dados: CriarFiltroRequest): Promise<IdResposta> =>
  (await api.post(`${ADMIN}/${clientId}/filtros`, dados)).data;

/** 204. */
export const excluirFiltroDados = async (clientId: number, filtroId: number): Promise<void> => {
  await api.delete(`${ADMIN}/${clientId}/filtros/${filtroId}`);
};

/** Home e acessos da pessoa (registra evento `VerComo`). */
export const getVerComo = async (clientId: number, pessoaId: number): Promise<VerComoViewModel> =>
  (await api.get(`${ADMIN}/${clientId}/ver-como/${pessoaId}`)).data;

/** Embed com abas e filtros da pessoa (token do admin); 404 sem acesso para ela. */
export const getEmbedVerComo = async (
  clientId: number,
  pessoaId: number,
  reportId: number
): Promise<EmbedViewModel> =>
  (await api.get(`${ADMIN}/${clientId}/ver-como/${pessoaId}/reports/${reportId}/embed`)).data;

export const getMatriz = async (clientId: number): Promise<MatrizViewModel> =>
  (await api.get(`${ADMIN}/${clientId}/matriz`)).data;

/** 200. Copia concessões pessoais e grupos; não copia papel nem filtros. */
export const copiarAcessos = async (
  clientId: number,
  destinoId: number,
  origemId: number
): Promise<CopiarAcessosResposta> =>
  (await api.post(`${ADMIN}/${clientId}/pessoas/${destinoId}/copiar-de/${origemId}`)).data;

/** `aplicar=false` é ensaio (não grava). */
export const importarAcessos = async (
  clientId: number,
  dados: ImportarAcessosRequest,
  aplicar: boolean
): Promise<ImportacaoResultado> =>
  (await api.post(`${ADMIN}/${clientId}/importar`, dados, { params: { aplicar } })).data;

export const getUsoResumo = async (clientId: number, semanas = 8): Promise<UsoResumoViewModel> =>
  (await api.get(`${ADMIN}/${clientId}/uso/resumo`, { params: { semanas } })).data;

export const getGrants = async (clientId: number, reportId?: number): Promise<GrantViewModel[]> =>
  (await api.get(`${ADMIN}/${clientId}/grants`, { params: { reportId } })).data;

/** 201. 400 relatório não publicado; 404 pessoa sem vínculo ativo. */
export const criarGrant = async (clientId: number, dados: CriarGrantRequest): Promise<CriarGrantResposta> =>
  (await api.post(`${ADMIN}/${clientId}/grants`, dados)).data;

/** 204. Inativa a concessão. */
export const revogarGrant = async (clientId: number, grantId: number): Promise<void> => {
  await api.delete(`${ADMIN}/${clientId}/grants/${grantId}`);
};

/** 50 por página, mais recentes primeiro; `de` padrão = 7 dias atrás. */
export const getEventos = async (clientId: number, filtro: FiltroEventos = {}): Promise<EventosPagina> =>
  (await api.get(`${ADMIN}/${clientId}/events`, { params: filtro })).data;
